// Apply MutationObserver fix IMMEDIATELY before ANY other imports
import "@/utils/mutationObserverFix.js";
// Guard Chart.js animator against orphan-chart crashes (see file for detail).
import "@/utils/chartJsSafetyPatch.js";

import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import Toast from "vue-toastification";
import ButtonSpinner from "@/components/globals/ButtonSpinner";
import ModernLoader from "@/components/ModernLoader";
import { v4 as uuidv4 } from "uuid";
import { configureCompat } from "vue";
import { ModalUtilsPlugin } from "@/utils/modalUtils.js";

// Configure Vue 3 compatibility mode
configureCompat({
  GLOBAL_MOUNT: false,
  GLOBAL_EXTEND: false,
  GLOBAL_PROTOTYPE: false,
  GLOBAL_SET: false,
  GLOBAL_DELETE: false,
  GLOBAL_OBSERVABLE: false,
  CONFIG_KEY_CODES: false,
  CONFIG_PRODUCTION_TIP: false,
  INSTANCE_SCOPED_SLOTS: false,
  INSTANCE_EVENT_EMITTER: false,
  INSTANCE_EVENT_HOOKS: false,
  INSTANCE_CHILDREN: false,
  INSTANCE_LISTENERS: false,
  INSTANCE_ATTRS_CLASS_STYLE: false,
  OPTIONS_DATA_FN: false,
  OPTIONS_DATA_MERGE: false,
  OPTIONS_BEFORE_DESTROY: false,
  OPTIONS_DESTROYED: false,
  RENDER_FUNCTION: false,
  FILTERS: false,
  TRANSITION_CLASSES: false,
  CUSTOM_DIR: false,
  ATTR_FALSE_VALUE: false,
  ATTR_ENUMERATED_COERCION: false,
  TRANSITION_GROUP_ROOT: false,
  COMPONENT_ASYNC: false,
  COMPONENT_FUNCTIONAL: false,
  COMPONENT_V_MODEL: false,
  WATCH_ARRAY: false,
  V_ON_KEYCODE_MODIFIER: false,
  NATIVE_TEMPLATE: false,
  COMPILER_INLINE_TEMPLATE: false,
  COMPILER_FILTERS: false,
});

uuidv4();

// loads the Icon plugin
window._ = require("lodash");

require("./styles/index.scss");
import "vue-toastification/dist/index.css";

// Create Vue app
const app = createApp(App);

// Create event bus for Vue 3 using mitt (external library) or simple emitter

// Simple event emitter for Vue 3
class EventBus {
  constructor() {
    this.events = {};
  }

  $emit(event, data) {
    if (this.events[event]) {
      this.events[event].forEach((callback) => callback(data));
    }
  }

  $on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  $off(event, callback) {
    if (this.events[event]) {
      const index = this.events[event].indexOf(callback);
      if (index > -1) {
        this.events[event].splice(index, 1);
      }
    }
  }
}

const eventBus = new EventBus();
app.config.globalProperties.$eventBus = eventBus;
window.Event = eventBus;

// Install plugins
app.use(router);
app.use(store);
app.use(ModalUtilsPlugin);
app.use(Toast, {
  position: "top-right",
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
});

// Manually add $toast global property for components
import { useToast } from "vue-toastification";
const toast = useToast();
app.config.globalProperties.$toast = toast;

import {
  createBootstrap,
  BNav,
  BButton,
  BButtonGroup,
  BAlert,
  BToast,
  BFormFile,
  BTable,
  BProgress,
  BInputGroup,
  BFormSelect,
  BFormSelectOptionGroup,
  BFormSelectOption,
  BDropdown,
  BModal,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
  BFormTextarea,
  BFormCheckbox,
  BNavItem,
  BFormGroup,
  BFormInvalidFeedback,
  vBTooltip,
} from "bootstrap-vue-next";
import VueGoodTable from "vue-good-table-next";
import vue3StarRatings from "vue3-star-ratings";
import "vue-good-table-next/dist/vue-good-table-next.css";

//import firebaseMessaging from "firebase";
import { getMessaging } from "firebase/messaging";
app.config.globalProperties.$messaging = getMessaging;

// Import Bootstrap an BootstrapVue CSS files (order is important)
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue-next/dist/bootstrap-vue-next.css";
import "bootstrap-icons/font/bootstrap-icons.css";
// Fix the bootstrap-icons font path on the production build (must come after
// the line above so this @font-face wins). See the file's comment.
import "./styles/icon-font-fix.css";

// Make BootstrapVue available throughout your project with all components
app.use(
  createBootstrap({
    directives: {
      BTooltip: vBTooltip,
    },
  }),
);

// Use vue-good-table-next globally
app.use(VueGoodTable);

// Temporary bridge for $bvModal during migration
app.config.globalProperties.$bvModal = {
  show: (modalId) => {
    console.warn(
      `$bvModal.show('${modalId}') called - please migrate to v-model pattern`,
    );
    // Emit global event that components can listen for
    window.Event.$emit(`show-modal-${modalId}`);
  },
  hide: (modalId) => {
    console.warn(
      `$bvModal.hide('${modalId}') called - please migrate to v-model pattern`,
    );
    // Emit global event that components can listen for
    window.Event.$emit(`hide-modal-${modalId}`);
  },
};

// Add $snack for backward compatibility using vue-toastification
app.config.globalProperties.$snack = {
  success: (options) => {
    const message = typeof options === "string" ? options : options.text;
    toast.success(message);
  },
  danger: (options) => {
    const message = typeof options === "string" ? options : options.text;
    toast.error(message);
  },
  warning: (options) => {
    const message = typeof options === "string" ? options : options.text;
    toast.warning(message);
  },
  info: (options) => {
    const message = typeof options === "string" ? options : options.text;
    toast.info(message);
  },
};

// Register global component
app.component("button-spinner", ButtonSpinner);
app.component("modern-loader", ModernLoader);
app.component("BButton", BButton);
app.component("BAlert", BAlert);
app.component("BFormSelect", BFormSelect);
app.component("BFormSelectOption", BFormSelectOption);
app.component("BFormSelectOptionGroup", BFormSelectOptionGroup);
app.component("BDropdown", BDropdown);
app.component("BModal", BModal);
app.component("BFormInput", BFormInput);
app.component("BFormRadio", BFormRadio);
app.component("BFormRadioGroup", BFormRadioGroup);
app.component("BFormTextarea", BFormTextarea);
app.component("BFormCheckbox", BFormCheckbox);
app.component("BNavItem", BNavItem);
app.component("BFormGroup", BFormGroup);
app.component("BInputGroup", BInputGroup);
app.component("BNav", BNav);
app.component("BProgress", BProgress);
app.component("BTable", BTable);
app.component("BToast", BToast);
app.component("BFormFile", BFormFile);
app.component("BFormInvalidFeedback", BFormInvalidFeedback);
app.component("BButtonGroup", BButtonGroup);

// Register directives
app.directive("b-tooltip", vBTooltip);
app.component("vue3-star-ratings", vue3StarRatings);

// Version check for cache busting
async function checkVersion() {
  try {
    // Add timestamp to bypass cache
    const response = await fetch("/version.json?" + Date.now());
    const latest = await response.json();
    const stored = localStorage.getItem("app_version");

    if (stored && stored !== latest.version) {
      console.log(
        `New version detected: ${stored} → ${latest.version}, reloading...`,
      );
      localStorage.setItem("app_version", latest.version);
      // Hard reload to clear cache
      window.location.reload(true);
    } else if (!stored) {
      // First time, just store the version
      console.log(`Initial version stored: ${latest.version}`);
      localStorage.setItem("app_version", latest.version);
    } else {
      console.log(`Current version: ${latest.version}`);
    }
  } catch (error) {
    console.warn("Version check failed:", error);
    // Fail silently - don't block app startup
  }
}

// Check version before mounting
checkVersion()
  .then(() => {
    // Mount app after version check
    app.mount("#app");
  })
  .catch(() => {
    // If version check fails, still mount the app
    app.mount("#app");
  });
